import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { AppSidebar } from "@/components/AppSidebar";
import { Footer } from "@/components/Footer";
import { SplashScreen } from "@/components/SplashScreen";
import { WordOfDayModal } from "@/components/WordOfDayModal";
import { RadioProvider } from "@/context/RadioContext";
import { RadioBar } from "@/components/RadioBar";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Directory from "@/pages/Directory";
import Devotionals from "@/pages/Devotionals";
import Events from "@/pages/Events";
import Feed from "@/pages/Feed";
import PraiseReports from "@/pages/PraiseReports";
import TicketPass from "@/pages/TicketPass";
import Notifications from "@/pages/Notifications";
import Messages from "@/pages/Messages";
import Account from "@/pages/Account";
import SupporterDashboard from "@/pages/SupporterDashboard";
import PaymentResult from "@/pages/PaymentResult";
import PrayerWall from "@/pages/PrayerWall";
import MemberProfile from "@/pages/MemberProfile";
import Assistant from "@/pages/Assistant";
import Admin from "@/pages/Admin";
import AdminScanner from "@/pages/AdminScanner";
import Marketing from "@/pages/Marketing";
import Notes from "@/pages/Notes";
import PublicDocument from "@/pages/PublicDocument";
import Unsubscribe from "@/pages/Unsubscribe";
import PrayerCircle from "@/pages/PrayerCircle";
import SupporterWall from "@/pages/SupporterWall";
import OnAir from "@/pages/OnAir";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bone">
      <div className="w-10 h-10 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function Shell() {
  const { user } = useAuth();
  const location = useLocation();
  const bare = ["/login", "/register", "/verify-email", "/forgot-password", "/reset-password", "/payment/success", "/payment/cancel", "/unsubscribe"].includes(location.pathname) || location.pathname.startsWith("/doc/");
  const authed = !!user;

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      {!bare && (authed ? <AppSidebar /> : <Navbar />)}
      {authed && <WordOfDayModal />}
      <div className={!bare && authed ? "md:pl-64" : ""}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/directory" element={<Protected><Directory /></Protected>} />
          <Route path="/members/:id" element={<Protected><MemberProfile /></Protected>} />
          <Route path="/devotionals" element={<Devotionals />} />
          <Route path="/events" element={<Events />} />
          <Route path="/onair" element={<OnAir />} />
          <Route path="/forum" element={<Protected><Feed /></Protected>} />
          <Route path="/feed" element={<Protected><Feed /></Protected>} />
          <Route path="/praise" element={<Protected><PraiseReports /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/ticket/:id" element={<Protected><TicketPass /></Protected>} />
          <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="/supporter" element={<Protected><SupporterDashboard /></Protected>} />
          <Route path="/payment/success" element={<PaymentResult mode="success" />} />
          <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
          <Route path="/prayers" element={<Protected><PrayerWall /></Protected>} />
          <Route path="/circle" element={<Protected><PrayerCircle /></Protected>} />
          <Route path="/supporters" element={<SupporterWall />} />
          <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="/admin/scanner" element={<Protected admin><AdminScanner /></Protected>} />
          <Route path="/marketing" element={<Protected admin><Marketing /></Protected>} />
          <Route path="/notes" element={<Protected><Notes /></Protected>} />
          <Route path="/doc/:id" element={<PublicDocument />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
        </Routes>
        {!bare && !["/assistant", "/messages"].includes(location.pathname) && <Footer />}
      </div>
      {!bare && <RadioBar />}
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem("ihimm_splash_seen"));

  const finishSplash = () => {
    sessionStorage.setItem("ihimm_splash_seen", "1");
    setShowSplash(false);
  };

  return (
    <div className="App">
      {showSplash && <SplashScreen onDone={finishSplash} />}
      <BrowserRouter>
        <AuthProvider>
          <RadioProvider>
            <SmoothScroll>
              <Shell />
            </SmoothScroll>
            <Toaster position="top-center" toastOptions={{ style: { fontFamily: "Manrope, sans-serif" } }} />
          </RadioProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
